/**
 * The AI knobs that were env variables until CF-15 (config-plan §1d, §4f; decision 26, Noah 2026-10-13).
 * Two groups:
 *
 *   ai.lab           the AI lab's spending caps and how long a run's client values are kept. Read by the
 *                    API (services/ai-lab/ai-lab-runs.ts labCaps: estimate, create, approve, the spend
 *                    view; ai-lab-retention.ts aiLabRetentionDays: the daily purge) and by the worker per
 *                    job (WORKER/jobs/ai-lab-run-handler.ts: the monthly cap, through loadSettings).
 *   ai.corrections   the corrections knowledge bank (EN-3): a reviewed AI lease term is recorded in
 *                    ai_term_reviews (API services/property-management/ai-term-reviews.ts).
 *
 * Zero change on the day: no row exists, so each value is its env variable as the code read it before
 * (`envParse` keeps the old spelling rules: a positive number for the caps, a whole number ≥ 1 for the
 * retention, anything but "off" for the bank), else today's default. A value outside the bounds below
 * falls to the default, as an unreadable one always did.
 *
 * Who: editPermission feature:system_settings (production: Admin and Super Admin), not ai_lab_approve —
 * the approvers are the people the caps hold back, so the matrix can widen approval later without
 * handing over the monthly ceiling. Every save needs a reason. Edited on Settings → AI → Caps & corrections
 * (M286 idea 17; on App settings before, which `editedOn` now keeps them off), by AIG accounts only
 * (`aigOnly`: Noah 2026-10-08 and 2026-10-16, AIG controls the AI area while it pays for AI; a client admin
 * with feature:system_settings neither sees nor changes these groups).
 */
import { z } from 'zod';
import { defineGroup } from '../define.js';
/** The env text as a number, as `Number(...)` read it before CF-15 ('' is unset; ' ' → 0, refused). */
const envNumber = (raw) => Number(raw);
export const aiLab = defineGroup('ai.lab', {
    label: 'AI lab',
    editPermission: 'feature:system_settings',
    requireReason: true,
    aigOnly: true,
    editedOn: 'Settings → AI → Caps & corrections',
    settings: {
        run_cap_usd: {
            type: z.number().positive().max(100),
            default: 2,
            label: 'Cost cap per run (USD)',
            help: 'A run estimated above this is refused, unless an approver confirms it (its cap then becomes the estimate × 1.5).',
            env: 'AI_LAB_RUN_CAP_USD',
            envParse: envNumber,
        },
        monthly_cap_usd: {
            type: z.number().positive().max(1000),
            default: 25,
            label: 'Monthly cost cap (USD)',
            help: 'All AI lab spend in a calendar month (UTC). A run that would pass it is refused, and a running one stops at it. Nobody can confirm past it.',
            env: 'AI_LAB_MONTHLY_CAP_USD',
            envParse: envNumber,
        },
        approval_usd: {
            type: z.number().positive().max(100),
            default: 1,
            label: 'Needs approval above (USD)',
            help: 'A run estimated above this waits for an approver before it starts.',
            env: 'AI_LAB_APPROVAL_USD',
            envParse: envNumber,
        },
        retention_days: {
            type: z.number().int().min(1).max(3650),
            default: 90,
            label: 'Keep run values for (days)',
            help: "After this many days a run's client values (the models' answers, the values they were scored against, the cached document text) are deleted in the nightly pass; its costs, scores and verdicts stay.",
            env: 'AI_LAB_RETENTION_DAYS',
            envParse: envNumber,
        },
    },
});
export const aiCorrections = defineGroup('ai.corrections', {
    label: 'AI corrections bank',
    editPermission: 'feature:system_settings',
    requireReason: true,
    aigOnly: true,
    editedOn: 'Settings → AI → Caps & corrections',
    settings: {
        bank_enabled: {
            type: z.boolean(),
            default: true,
            label: 'Record reviewed AI lease terms',
            help: 'On: each AI-proposed lease term a reviewer confirms, corrects or rejects is recorded, and the review screen shows how often the AI was right. Off: nothing is recorded and the hint disappears; the review itself works the same.',
            env: 'AI_CORRECTIONS_BANK',
            envParse: (raw) => raw.trim().toLowerCase() !== 'off',
        },
    },
});
//# sourceMappingURL=ai.js.map